import { cn } from '@/lib/utils';
import { formatDurationMs, type BuildDuration, type BuildTimeSummary, type StageRow } from '@/lib/stageTimeline';

/** Dashboard stat body for summariseBuildTimes: the median, and how many of
 *  the builds in range it is drawn from, so a figure resting on a handful of
 *  tracked builds says so. */
export function BuildTimeSummaryValue({ summary }: { summary: BuildTimeSummary | null | undefined }) {
  return (
    <>
      <div className="text-2xl font-bold">{summary?.medianMs != null ? formatDurationMs(summary.medianMs) : '—'}</div>
      <p
        className="text-xs text-muted-foreground"
        title="Median assembly time of builds whose timeline was tracked, with paused time (unless the pause was rejected) left out. Builds whose checklist was ticked after the work are left out; where the stress test predates the checklist, the build is measured from the stress test."
      >
        {summary ? `Median · ${summary.counted} of ${summary.total} builds tracked` : 'Start to sign-off'}
      </p>
    </>
  );
}

/** A build duration as assessed by assessBuildDuration: the figure when it can
 *  be trusted, a lower bound when it came from the stress test, and "Not
 *  tracked" rather than a false number when neither holds.
 *
 *  Shows the assembly duration (pauses out); `gross` shows the order
 *  duration (pauses in) instead. */
export function BuildDurationValue({ duration, className, gross = false }: { duration: BuildDuration | null | undefined; className?: string; gross?: boolean }) {
  const label = gross ? duration?.grossLabel : duration?.label;
  if (!label && duration?.kind !== 'untracked') return <span className={className}>—</span>;
  if (!duration) return <span className={className}>—</span>;
  if (duration.kind === 'untracked') {
    return (
      <span className={cn('text-amber-700 dark:text-amber-500', className)} title={duration.reason ?? undefined}>
        Not tracked
      </span>
    );
  }
  return (
    <span
      className={cn('font-mono', duration.kind === 'from_stress_test' && 'text-amber-700 dark:text-amber-500', className)}
      title={duration.reason ?? undefined}
    >
      {label}
    </span>
  );
}

/** The one-line explanation for a duration that isn't plainly tracked. */
export function BuildDurationNote({ duration, className }: { duration: BuildDuration | null | undefined; className?: string }) {
  if (!duration?.reason) return null;
  return <p className={cn('text-xs text-amber-700 dark:text-amber-500', className)}>{duration.reason}</p>;
}

/** "3h 10m paused · order duration 1d 2h" under a duration whose span
 *  contained pauses; nothing when it did not. */
export function PausedTimeNote({ duration, className }: { duration: BuildDuration | null | undefined; className?: string }) {
  if (!duration?.pausedMs || duration.kind === 'untracked') return null;
  return (
    <p className={cn('text-xs text-muted-foreground', className)}>
      {formatDurationMs(duration.pausedMs)} paused, not counted · order duration {duration.grossLabel}
    </p>
  );
}

/**
 * The build timeline block in the review dialogs: a row per stage, then the
 * assembly duration (pauses out) and, when anything was paused, the order
 * duration (pauses in).
 */
export function ReviewBuildTimeline({
  stages, duration, lunchMs, pendingPauses = 0,
}: {
  stages: StageRow[];
  duration: BuildDuration | null;
  lunchMs: number;
  pendingPauses?: number;
}) {
  if (!stages.length) return null;
  const paused = !!duration?.pausedMs && duration.kind !== 'untracked';
  return (
    <div className="space-y-1">
      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Build Timeline</p>
      <div className="p-3 bg-muted rounded-lg space-y-1">
        {stages.map((s, i) => (
          <div key={i} className="flex justify-between text-sm">
            <span className="text-muted-foreground">
              {s.stage}
              {s.estimated && (
                <span className="ml-1 text-[10px] uppercase opacity-60" title="Estimated from section weights — no explicit checklist timestamps recorded">est.</span>
              )}
              {!!s.pausedMs && (
                <span className="ml-1 text-[10px] text-amber-700 dark:text-amber-500" title="Paused time inside this stage, not counted">
                  ({formatDurationMs(s.pausedMs)} paused)
                </span>
              )}
            </span>
            <span className={`font-mono font-medium ${s.estimated ? 'italic opacity-80' : ''}`}>{s.estimated ? `~${s.duration}` : s.duration}</span>
          </div>
        ))}
        {duration?.recordedLabel && (
          <div className="flex justify-between text-sm pt-1 border-t border-border/50">
            <span className="font-medium">{paused ? 'Assembly time' : 'Total'}</span>
            <BuildDurationValue duration={duration} className="font-medium" />
          </div>
        )}
        {paused && (
          <>
            <div className="flex justify-between text-sm text-amber-700 dark:text-amber-500">
              <span>Paused, not counted</span>
              <span className="font-mono">−{formatDurationMs(duration!.pausedMs)}</span>
            </div>
            <div className="flex justify-between text-sm text-muted-foreground">
              <span>Order duration</span>
              <span className="font-mono">{duration!.grossLabel}</span>
            </div>
          </>
        )}
        {duration?.kind === 'tracked' && lunchMs > 0 && (
          <p className="text-xs text-muted-foreground text-right">
            {Math.round(lunchMs / 3_600_000)}h lunch break included
          </p>
        )}
        {paused && pendingPauses > 0 && (
          <p className="text-xs text-muted-foreground text-right">
            Pauses awaiting a decision are left out for now; rejecting one adds its time back.
          </p>
        )}
        <BuildDurationNote duration={duration} className="text-right" />
      </div>
    </div>
  );
}
